import {
  ConnectableElement,
  ConnectorElement,
  ConnectorInstance,
  ConnectorInstanceElement,
  DatapointInstance,
  InlineDataPointInstance
} from '@olive/olive-types';
import ConnectivityHolder from '../data-layer/ConnectivityHolder';
import {
  isConnectorDefinition,
  isConnectorInstance,
  isConnectorTask,
  isDatapointDefinition,
  isDatapointInstance,
  isInlineDatapointInstance,
} from '@olive/types-util';

/**
 * Extracts a datapoint from a connector element
 * @param element The connector element
 * @param connectivityHolder The connectivity holder instance
 * @returns The resolved connectable element
 */
export function getDatapointFromConnectorElement(
  element: ConnectorElement | ConnectorInstanceElement,
  connectivityHolder: ConnectivityHolder
): ConnectableElement {
  if (typeof (element) === 'string') {
    return connectivityHolder.get(element) as ConnectableElement;
  }
  if (isConnectorTask(element)) {
    return getDatapointFromConnectorElement(element.element, connectivityHolder);
  }
  if (isDatapointInstance(element)) {
    return getDatapointFromConnectorElement(element.datapoint, connectivityHolder);
  }
  if (isDatapointDefinition(element) || isConnectorDefinition(element) || isConnectorInstance(element)) {
    return element as ConnectableElement;
  }
  return element as ConnectableElement;
}

/**
 * Finds a connectivity item based on its type
 * @param item The item to find
 * @param holder The connectivity holder
 * @returns The located connectivity item
 */
export function findConnectivityItem(
  item: string | DatapointInstance | InlineDataPointInstance | ConnectorInstance | ConnectorInstanceElement,
  holder: ConnectivityHolder
) {
  // Handle string case
  if (typeof item === 'string') {
    return holder.get(item);
  }

  // Handle object cases
  const extractedItem = extractConnectivityItemInfo(item);

  // If we have an ID but no extracted item, get it from the holder
  if (extractedItem.id && !extractedItem.item) {
    return holder.get(extractedItem.id);
  }

  return extractedItem.item;
}

/**
 * Helper function to extract item information based on type
 * @private
 */
function extractConnectivityItemInfo(
  item: DatapointInstance | InlineDataPointInstance | ConnectorInstance | ConnectorInstanceElement
) {
  const result = { id: '', item: null };

  if (isInlineDatapointInstance(item)) {
    result.item = item;
    return result;
  }

  if (isDatapointInstance(item)) {
    const datapoint = item.datapoint;
    if (typeof datapoint === 'string') {
      result.id = datapoint;
    } else {
      result.item = datapoint;
    }
    return result;
  }

  if (isConnectorInstance(item)) {
    const datapoint = item.elements[0];
    if (typeof datapoint === 'string') {
      result.id = datapoint;
    } else {
      result.item = datapoint as ConnectorInstance;
    }
    return result;
  }

  console.error('Unknown item type: ', item);
  return result;
} 